import React, { useState, useEffect, useRef } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import { toast } from "react-toastify";
import CheckIcon from "@mui/icons-material/Check";
import {
  FULFILLED,
  PENDING,
} from "../../../redux/constants/actionStatusConstants";
import useSession from "../../../hooks/useSession";
import { useDispatch } from "react-redux";
import {
  postJob,
  postWork,
  updateJob,
} from "../../../redux/actions/projectActions";
import { useStatus } from "../../../hooks/useStatus";
import { useProjectDetails } from "../../../hooks/useProject";
import ProjectSubmittedModal from "../ProjectSubmittedModal";
import SelectFirefighterModal from "../SelectFirefighterModal";

const useStyles = makeStyles((theme) => ({
  infoContainer: {
    display: "flex",
    paddingLeft: "26% !important",
    paddingRight: "26% !important",
    alignItems: "center !important",
    paddingBottom: "3rem !important",

    [theme.breakpoints.down("sm")]: {
      paddingLeft: "5% !important",
      paddingRight: "5% !important",
    },
  },
  estimatePrices: {
    width: "100%",
  },
  row: {
    display: "flex",
  },
  title: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    width: "95%",
  },
  accordionContainer: {
    borderRadius: "12px",
    border: "1px solid #F1F1F1",
    marginBottom: "5rem",
  },
}));

const ProjectSubmission = ({
  expanded,
  setExpanded,
  handleChange,
  handleSubmit,
  selectedService,
  getValues,
  isAbleToSubmit,
  scrollToPanel,
  refs,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSession();
  const { projectDetails } = useProjectDetails();
  const { status } = useStatus(postJob);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [isViewed, setIsViewed] = useState(false);

  const defaultAddress = user?.address;
  const idDefaultAddress = user?.address?.id;

  const [openModal, setOpenModal] = useState(false);
  const [openFirefightersModal, setOpenFirefightersModal] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [formData, setFormData] = useState({});
  const [showPhoenixCounter, setShowPhoenixCounter] = useState(false);
  const isSubmittingRef = useRef(false);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleOpenFirefighterModal = () => setOpenFirefightersModal(true);
  const handleCloseFirefighterModal = () => setOpenFirefightersModal(false);

  const postNewWork = async (data) => {
    try {
      const isDefaultAddress =
        data.addressAttributes?.street === defaultAddress?.street &&
        data.addressAttributes?.city === defaultAddress?.city &&
        data.addressAttributes?.state === defaultAddress?.state &&
        data.addressAttributes?.country === defaultAddress?.country &&
        data.addressAttributes?.zip === defaultAddress?.zip &&
        data.addressAttributes?.unit === defaultAddress?.unitOrApt &&
        idDefaultAddress;

      const payload = {
        work: {
          title: data.title,
          description: data.description,
          notes: data.notes,
          images: data.images,
          address_attributes: {
            fullAddress: data.addressAttributes.fullAddress,
            state: data.addressAttributes.state,
            city: data.addressAttributes.city,
            zip: data.addressAttributes.zip,
            street: data.addressAttributes.street,
            country: data.addressAttributes.country,
            unit: data.unitOrApt,
            id: data.addressAttributes.id,
          },
          address_id: data.addressAttributes.id,
          preferred_date: data.preferredDate,
          special_equipment: data.special_equipment,
          equipment_details: data.equipment_details,
          service_id: selectedService[0]?.id,
          fire_fighter_works_attributes: data.fire_fighter_works_attributes,
          ...(isDefaultAddress && { address_id: idDefaultAddress }),
          user_availabilities_attributes: data.dateTimes?.map((date) => ({
            available_at: date,
          })),
        },
      };
      const response = await dispatch(postWork(payload));
      return response;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    if (expanded === "panel3") {
      setIsViewed(false);
    }
  }, [expanded]);

  const submitProject = async (data) => {
    if (!isAbleToSubmit) {
      return toast.error(
        <Typography color="error">
          Please verify the form again. Some required fields are missing
        </Typography>,
        {
          position: "top-center",
        }
      );
    }
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setUpdateLoading(true);
    try {
      const values = getValues();
      if (!!user?.hasPaymentMethod || values.isFranchiseZipCode) {
        let response = null;
        if (!projectDetails?.id && selectedService) {
          response = await dispatch(
            postJob({
              job: {
                ...data,
                serviceId: selectedService[0]?.id,
                discount_coupon_code: values.discount_coupon_code,
              },
            })
          );

          if (response?.payload) {
            localStorage.setItem("type", response.payload.type);
            setShowPhoenixCounter(false);
            setProjectId(response.payload.id);
          }
        } else {
          if (selectedService) {
            response = await dispatch(
              updateJob({
                jobId: projectDetails.id,
                job: { ...data, serviceId: selectedService[0]?.id },
              })
            );
          }
        }

        if (response?.meta?.requestStatus === FULFILLED) {
          toast.success(
            <Typography color="success">
              Project was successfully{" "}
              {projectDetails?.id ? "updated" : "created"}.
            </Typography>,
            {
              position: "top-center",
            }
          );
          projectDetails?.id && navigate("/success-project");
        } else {
          toast.error(
            <Typography color="error">{`${response?.error?.message}. Please verify the form again.`}</Typography>,
            {
              position: "top-center",
            }
          );
          if (response?.error?.message.includes("address")) {
            setExpanded("panel1");
            scrollToPanel("panel1", refs.locationRef);
          } else if (
            response?.error?.message.includes("description") ||
            response?.error?.message.includes("title")
          ) {
            setExpanded("panel2");
            scrollToPanel("panel2", refs.descriptionRef);
          }
        }
      } else {
        if (user === null) {
          toast.error(
            <Typography color="error">
              Please complete your contact information.
            </Typography>,
            {
              position: "top-center",
            }
          );
        } else {
          if (!user?.verifiedAt) {
            toast.error(
              <Typography color="error">Please verify your account.</Typography>,
              {
                position: "top-center",
              }
            );
          } else {
            if (!!!user?.hasPaymentMethod) {
              toast.error(
                <Typography color="error">
                  Please add a payment method to continue.
                </Typography>,
                {
                  position: "top-center",
                }
              );
            } else {
              if (localStorage.getItem("fireFightersNear") === "0") {
                toast.error(
                  <Typography color="error">
                    Please add a new address.
                  </Typography>,
                  {
                    position: "top-center",
                  }
                );
              }
            }
          }
        }
      }
    } finally {
      isSubmittingRef.current = false;
      setUpdateLoading(false);
    }
  };

  const handleSelectFirefightersSubmit = async (selectedFirefighters = []) => {
    const updatedData = {
      ...formData,
      fire_fighter_works_attributes: selectedFirefighters.map((id) => ({
        fire_fighter_id: id,
      })),
    };
    try {
      const response = await postNewWork(updatedData);
      if (response?.meta?.requestStatus === FULFILLED) {
        setShowPhoenixCounter(true);
        setProjectId(response.payload.work.id);
        localStorage.setItem("type", "Work");
        toast.success(
          <Typography color="success">
            Project was successfully created.
          </Typography>,
          {
            position: "top-center",
          }
        );
      } else {
        toast.error(
          <Typography color="error">
            {response?.error?.message || "Error creating work."}
          </Typography>
        );
      }
    } catch (error) {
      toast.error(
        <Typography color="error">An unexpected error occurred.</Typography>
      );
    } finally {
      handleCloseFirefighterModal();
    }
  };

  const handleContinue = async (data) => {
    const updatedData = { ...data, ...getValues() };
    const addressAttributes = updatedData.addressAttributes || {};
    setFormData(updatedData);

    if (!projectDetails?.id && selectedService && isAbleToSubmit) {
      if (data.isPhoenixLocation) {
        handleOpenFirefighterModal();
        return;
      }
    }
    const jobData = {
      images: updatedData.images,
      preferred_date: updatedData.preferredDate,
      special_equipment: updatedData.special_equipment,
      description: updatedData.description,
      equipment_details: updatedData.equipment_details,
      notes: updatedData.notes,
      title: updatedData.title,
      address_attributes: {
        full_address: addressAttributes.fullAddress,
        state: addressAttributes.state,
        city: addressAttributes.city,
        zip: addressAttributes.zip,
        street: addressAttributes.street,
        country: addressAttributes.country,
        unit: updatedData.unitOrApt,
      },
    };
    submitProject(jobData);
  };

  React.useEffect(() => {
    projectId && handleOpenModal();
  }, [projectId]);

  return (
    <>
      <Accordion
        className={classes.accordionContainer}
        expanded={expanded === "panel3"}
        onChange={handleChange("panel3")}
      >
        <AccordionSummary aria-controls="panel3a-content" id="panel3a-header">
          <div className={classes.title}>
            <Typography variant="title1">Review & Submit</Typography>
            {isViewed && (
              <div
                className={classes.row}
                style={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Button
                  variant="text"
                  sx={{
                    color: "#2b8ced",
                    fontFamily: "Urbanist-Medium",
                    display: "flex",
                    fontsize: 14,
                    fontWeight: 500,
                    fontStretch: "normal",
                    fontStyle: "normal",
                    lineHeight: 1.43,
                    letterSpacing: "normal",
                    textAlign: "left",
                  }}
                >
                  View
                </Button>
                <CheckIcon color="green" />
              </div>
            )}
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.infoContainer}>
          <div className={classes.estimatePrices} />
          <Typography
            sx={{
              color: "black !important",
              textAlign: "center",
              mt: "0.5rem",
            }}
            variant="body1"
          >
            Review your project details and submit when you are ready. Questions? Check out our{" "}
            <b
              style={{ cursor: "pointer" }}
              onClick={() =>
                window.open(`${process.env.REACT_APP_WEB_URL}/faq`)
              }
            >
              FAQs
            </b>
          </Typography>
          <Button
            sx={{
              width: { md: "11.9rem", xs: "100%" },
              mt: "2rem",
            }}
            disabled={status?.status === PENDING || updateLoading}
            variant="contained"
            onClick={handleSubmit(handleContinue)}
          >
            {(!!user?.hasPaymentMethod || !!getValues("isFranchiseZipCode")) &&
            localStorage.getItem("fireFightersNear") !== "0"
              ? "Submit Project"
              : "Continue"}
          </Button>
        </AccordionDetails>
      </Accordion>

      {openModal && projectId && (
        <ProjectSubmittedModal
          open={openModal}
          onClose={handleCloseModal}
          projectId={projectId}
          showPhoenixCounter={showPhoenixCounter}
          isFranchiseJob={!!getValues("isFranchiseZipCode")}
        />
      )}

      <SelectFirefighterModal
        open={openFirefightersModal}
        onClose={handleCloseFirefighterModal}
        fullAddress={formData.addressAttributes?.fullAddress}
        onSubmit={handleSelectFirefightersSubmit}
        setGlobalValue={(key, value) =>
          setFormData({ ...formData, [key]: value })
        }
      />
    </>
  );
};

export default ProjectSubmission;
